import type { AxiosError } from 'axios';
import type { ReactNode } from 'react';
import { Box, Button, Center, Container, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type { Contract, ContractLine, Offer, PriceListItem, Space } from '@/api/generated/models';
import type { CardTab } from '@/components/common';
import type {
  SpaceDraftHandle,
  SpaceDraftState
} from '@/features/contracts/components/ContractSpaceBreakdown';
import type { DraftSummary, DraftValue } from '@/utils/useItemsDraft';
import {
  getGetContractQueryKey,
  getListContractLinesQueryKey,
  getListContractsQueryKey,
  useAcceptContract,
  useActivate,
  useCancelContract,
  useCloseContract,
  useComplete,
  useDeactivateContract,
  useGetContract,
  useHold,
  useListContractLines,
  useRejectContract,
  useSaveContractLines,
  useSendContract
} from '@/api/generated/contracts/contracts';
import { ContractContractType } from '@/api/generated/models/contractContractType';
import { ContractStatus } from '@/api/generated/models/contractStatus';
import { useGetOffer } from '@/api/generated/offers/offers';
import { useListUnits } from '@/api/generated/units/units';
import {
  getListCoveredSpacesQueryKey,
  getListLineAllocationsQueryKey,
  getListSpaceLinePlansByContractQueryKey,
  useListCoveredSpaces,
  useListLineAllocations
} from '@/api/generated/work-structure/work-structure';
import {
  IconFigmaAlertTriangle,
  IconFigmaCheck,
  IconFigmaDocument,
  IconFigmaEuro,
  IconFigmaInfo,
  IconFigmaXCircle
} from '@/assets/icons';
import {
  AddActionButton,
  BackButton,
  CardSection,
  CardTabs,
  ConfirmActionModal,
  DRAFT_ROW_BACKGROUND,
  DraftNumberInput,
  ItemsSaveBar,
  ItemsSaveStatus,
  LinkValue,
  ListPagination,
  MobileActionBar,
  MutationButton,
  NewItemBadge,
  StatusBanner,
  StatusPill,
  StatusTracker,
  TABLE_ITEM_CODE_COL_WIDTH,
  tableRowStyle,
  Toast,
  UndoRemoveButton,
  useUnsavedChangesGuard
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { CancelContractModal } from '@/features/contracts/components/CancelContractModal';
import { ContractItemPickerModal } from '@/features/contracts/components/ContractItemPickerModal';
import { ContractLineAllocationModal } from '@/features/contracts/components/ContractLineAllocationModal';
import { ContractLineFormModal } from '@/features/contracts/components/ContractLineFormModal';
import { ContractSpaceBreakdown } from '@/features/contracts/components/ContractSpaceBreakdown';
import { ContractSpaceSelectionModal } from '@/features/contracts/components/ContractSpaceSelectionModal';
import { RejectContractModal } from '@/features/contracts/components/RejectContractModal';
import { SendContractModal } from '@/features/contracts/components/SendContractModal';
import {
  buildContractTrackerSteps,
  contractStatusLabelKey,
  statusConfig
} from '@/features/contracts/contractDisplay';
import { formatObjectType } from '@/features/price-lists/objectDisplay';
import { findUnitLabel } from '@/features/units/unitDisplay';
import {
  currentLocale,
  formatAmount,
  formatDate,
  formatDateRange,
  formatNumber,
  formatWeekYearRangeFromDates,
  otherPartyName
} from '@/utils/format';
import { openBinaryInNewTab } from '@/utils/openBinaryInNewTab';
import { useItemsDraft } from '@/utils/useItemsDraft';
import { useLocalPagination } from '@/utils/useLocalPagination';
import { useSavedMessage } from '@/utils/useSavedMessage';

const divider = <div style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }} />;

// Isti obrazac kao ContractLineFormModal/OfferLineFormModal — backend već vraća specifičan, lokalizirani razlog.
const contractLineErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

const formatHours = (value: number) =>
  `${value.toLocaleString(currentLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;

/** Uklanjanje stavke u nacrtu — stavka se briše tek kad se spreme stavke. */
const RemoveLineButton = ({ onClick }: { onClick: () => void }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') onClick();
    }}
    style={{ cursor: 'pointer', color: 'var(--color-status-danger)' }}
  >
    <IconFigmaXCircle size={16} />
  </Box>
);

/** Redak stavki: spremljena stavka s primijenjenim nacrtom ili nova stavka iz nacrta. */
interface DisplayLine {
  key: string;
  lineId?: string;
  tempId?: string;
  line?: ContractLine;
  isNew: boolean;
  isRemoved: boolean;
  itemCode?: string;
  description?: string;
  unitId?: string;
  quantity: DraftValue;
  unitPrice: DraftValue;
  savedQuantity?: number;
  savedUnitPrice?: number;
  isChanged: boolean;
  quantityError: string | null;
  priceError: string | null;
}

export const ContractDetailPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthContext();
  const queryClient = useQueryClient();

  // refetchOnMount: 'always' — status se mijenja od druge strane (klijent
  // prihvaća/odbija), pa ne smijemo prikazati zastarjeli status iz keša kad
  // se otvori detalj s liste.
  const { data, isLoading } = useGetContract(id ?? '', {
    query: { refetchOnMount: 'always' }
  });
  const contract = data as Contract | undefined;

  const { data: linesData } = useListContractLines(id ?? '', {
    query: { refetchOnMount: 'always' }
  });
  const lines: ContractLine[] = (linesData ?? []).filter((line) => !line.validTo);
  const { data: allocationsData } = useListLineAllocations(id ?? '', { query: { enabled: !!id } });
  const { data: unitsData } = useListUnits();

  // Stavke se ne spremaju same — izmjene, nove i uklonjene stavke čekaju u nacrtu dok korisnik
  // ne klikne "Spremi stavke" (jedan zahtjev, jedna transakcija na backendu).
  const draft = useItemsDraft<PriceListItem>();
  const [justSaved, setJustSaved] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const nonNegativeError = t('itemsDraft.errorNonNegative');
  // Količina ne smije pasti ispod već raspoređene po prostorima (ContractService.updateLine) —
  // pokaži to odmah u polju, ne tek kad spremanje padne.
  const allocatedByLineId = new Map(
    (allocationsData ?? []).map((allocation) => [
      allocation.contractLineId,
      allocation.allocatedQty ?? 0
    ])
  );

  const displayLines: DisplayLine[] = [
    ...lines.map((line): DisplayLine => {
      const lineId = line.id ?? '';
      const savedQuantity = line.quantity ?? 0;
      const savedUnitPrice = line.unitPrice ?? 0;
      const quantity = draft.valueOf(lineId, 'quantity', savedQuantity);
      const unitPrice = draft.valueOf(lineId, 'unitPrice', savedUnitPrice);
      const isRemoved = draft.isRemoved(lineId);
      const allocated = allocatedByLineId.get(lineId) ?? 0;
      return {
        key: lineId,
        lineId,
        line,
        isNew: false,
        isRemoved,
        itemCode: line.itemCode,
        description: line.description,
        unitId: line.unitId,
        quantity,
        unitPrice,
        savedQuantity,
        savedUnitPrice,
        isChanged: !isRemoved && (quantity !== savedQuantity || unitPrice !== savedUnitPrice),
        quantityError: isRemoved
          ? null
          : quantity === ''
            ? nonNegativeError
            : quantity < allocated
              ? t('contractDetail.qtyBelowAllocated', { allocated })
              : null,
        priceError: !isRemoved && unitPrice === '' ? nonNegativeError : null
      };
    }),
    ...draft.added.map((item): DisplayLine => ({
      key: item.tempId,
      tempId: item.tempId,
      isNew: true,
      isRemoved: false,
      itemCode: item.meta.itemCode,
      description: item.meta.description,
      unitId: item.meta.unitId,
      quantity: item.values.quantity ?? 0,
      unitPrice: item.values.unitPrice ?? 0,
      // Početne vrijednosti nove stavke — polje se označi tek kad ga korisnik promijeni.
      savedQuantity: 0,
      savedUnitPrice: item.meta.unitPrice ?? 0,
      isChanged: false,
      quantityError: item.values.quantity === '' ? nonNegativeError : null,
      priceError: item.values.unitPrice === '' ? nonNegativeError : null
    }))
  ];
  const lineAmount = (line: DisplayLine) =>
    (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
  const changedCount = displayLines.filter((line) => line.isChanged).length;
  const removedCount = displayLines.filter((line) => line.isRemoved).length;
  const draftSummary: DraftSummary = {
    changed: changedCount,
    added: draft.added.length,
    removed: removedCount,
    errors: displayLines.filter((line) => line.quantityError || line.priceError).length,
    total: changedCount + draft.added.length + removedCount
  };

  const setLineValue = (line: DisplayLine, field: 'quantity' | 'unitPrice', value: DraftValue) => {
    setJustSaved(false);
    if (line.tempId) draft.setAddedValue(line.tempId, field, value);
    else if (line.lineId) draft.setValue(line.lineId, field, value);
  };
  const removeLine = (line: DisplayLine) => {
    setJustSaved(false);
    if (line.tempId) draft.discardAdded(line.tempId);
    else if (line.lineId) draft.toggleRemoved(line.lineId);
  };
  const addPriceListItems = (items: PriceListItem[]) => {
    setJustSaved(false);
    draft.addItems(
      items.map((item) => ({ meta: item, values: { quantity: 0, unitPrice: item.unitPrice ?? 0 } }))
    );
  };

  const {
    page: linesPage,
    setPage: setLinesPage,
    pageSize: linesPageSize,
    setPageSize: setLinesPageSize,
    totalPages: linesTotalPages,
    pageItems: pageRows
  } = useLocalPagination(displayLines);
  const pageLines = pageRows.flatMap((row) => (row.line ? [row.line] : []));

  const saveLinesMutation = useSaveContractLines({
    mutation: { meta: { skipGlobalErrorToast: true } }
  });

  const saveDraft = async (): Promise<boolean> => {
    if (!id || draftSummary.errors > 0) return false;
    setSaveError(null);
    try {
      await saveLinesMutation.mutateAsync({
        id,
        data: {
          removedLineIds: displayLines
            .filter((line) => line.isRemoved && line.lineId)
            .map((line) => line.lineId as string),
          updatedLines: displayLines
            .filter((line) => line.isChanged && line.lineId)
            .map((line) => ({
              lineId: line.lineId as string,
              quantity: Number(line.quantity),
              unitPrice: Number(line.unitPrice)
            })),
          addedLines: draft.added
            .filter((item) => item.meta.id)
            .map((item) => ({
              priceListItemId: item.meta.id as string,
              quantity: Number(item.values.quantity),
              unitPrice: Number(item.values.unitPrice)
            }))
        }
      });
      // Nacrt se briše tek kad su svježe stavke stigle — inače bi na trenutak bljesnulo staro stanje.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getListContractLinesQueryKey(id) }),
        queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(id) }),
        queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getListLineAllocationsQueryKey(id) })
      ]);
      draft.reset();
      setJustSaved(true);
      setSavedAt(
        new Date().toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
      );
      return true;
    } catch (error) {
      setSaveError(
        t('itemsDraft.saveFailed', { reason: contractLineErrorMessage(error, t('common.error')) })
      );
      return false;
    }
  };

  const discardDraft = () => {
    draft.reset();
    setSaveError(null);
  };

  // Razrada prostora drži vlastiti nacrt (po prostoru); stranica ga spaja sa stavkama u jedno
  // pitanje Odbaci/Ostani/Spremi pri izlasku, promjeni taba i akcijama.
  const spaceDraftRef = useRef<SpaceDraftHandle | null>(null);
  const [spaceDraftState, setSpaceDraftState] = useState<SpaceDraftState>({
    summary: { changed: 0, added: 0, removed: 0, errors: 0, total: 0 },
    notFullyAllocated: 0
  });
  const spaceSummary = spaceDraftState.summary;
  const combinedSummary: DraftSummary = {
    changed: draftSummary.changed + spaceSummary.changed,
    added: draftSummary.added + spaceSummary.added,
    removed: draftSummary.removed + spaceSummary.removed,
    errors: draftSummary.errors + spaceSummary.errors,
    total: draftSummary.total + spaceSummary.total
  };

  const { guard, modal: unsavedChangesModal } = useUnsavedChangesGuard({
    summary: combinedSummary,
    onSave: async () => {
      const linesSaved = draftSummary.total > 0 ? await saveDraft() : true;
      const spacesSaved =
        spaceSummary.total > 0 ? ((await spaceDraftRef.current?.save()) ?? true) : true;
      return linesSaved && spacesSaved;
    },
    onDiscard: () => {
      discardDraft();
      spaceDraftRef.current?.discard();
    }
  });

  const { data: linkedOfferData } = useGetOffer(contract?.offerId ?? '', {
    query: { enabled: !!contract?.offerId, refetchOnMount: 'always' }
  });
  const linkedOffer = linkedOfferData as Offer | undefined;

  const isPartnerView = !!user && !!contract && user.organisationId === contract.partnerOrgId;
  // CON-09 (obrnuti smjer): kod clientInitiated ugovora KLIJENT je taj koji izvorno gradi i šalje
  // DRAFT (kreira ga iz vlastite prihvaćene ponude preko "Kreiraj ugovor"), partner odlučuje —
  // obrnuto od normalnog toka. "Autorska" strana — ona koja smije uređivati/brisati/slati DRAFT
  // ili REJECTED ugovor i graditi razradu prostora — je zato partner u normalnom toku, klijent u
  // clientInitiated toku. NIJE isto što i golo isPartnerView: nakon odbijanja (REJECTED) OBJE
  // kopije dijele taj status (vidi ContractService.changeDecision), pa bi isPartnerView samo
  // propustio i onu stranu koja je zapravo odlučitelj, ne autor.
  const isDraftAuthor = !!contract && (contract.clientInitiated ? !isPartnerView : isPartnerView);

  // Razrada prostora — obje strane je vide na svojoj kopiji (klijentova kopija ima vlastiti klon
  // objekta i iste pokrivene prostore); uređuje je samo autorska strana (canManageWorkStructure).
  const { data: coveredSpacesData } = useListCoveredSpaces(id ?? '', {
    query: { enabled: !!id }
  });
  const coveredSpaces = (coveredSpacesData as Space[] | undefined) ?? [];

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [spaceSelectionModalOpen, setSpaceSelectionModalOpen] = useState(false);
  const [lineAllocationModalOpen, setLineAllocationModalOpen] = useState(false);
  const [activePlanningTab, setActivePlanningTab] = useState('lines');
  const [savedMessage, setSavedMessage] = useSavedMessage();
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const handlePreview = async () => {
    if (!id) return;
    setPreviewError(false);
    setIsPreviewLoading(true);
    try {
      await openBinaryInNewTab(`/api/v1/contracts/${id}/pdf`);
    } catch {
      setPreviewError(true);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Promjena statusa (slanje, prihvaćanje…) mijenja i ono što se smije raditi s prostorima i
  // stavkama — osvježi i razradu ovog ugovora, ne samo zaglavlje i listu, da radni paketi
  // odmah nakon prihvaćanja ne rade sa zastarjelim podacima.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(id) });
    queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListContractLinesQueryKey(id) });
    queryClient.invalidateQueries({ queryKey: getListCoveredSpacesQueryKey(id) });
    queryClient.invalidateQueries({ queryKey: getListSpaceLinePlansByContractQueryKey(id) });
  };

  const acceptMutation = useAcceptContract({ mutation: { onSuccess: invalidate } });
  const rejectMutation = useRejectContract({
    mutation: {
      onSuccess: () => {
        invalidate();
        setRejectModalOpen(false);
      }
    }
  });
  const sendMutation = useSendContract({
    mutation: {
      onSuccess: () => {
        invalidate();
        setSendModalOpen(false);
      }
    }
  });
  const cancelMutation = useCancelContract({
    mutation: {
      onSuccess: () => {
        invalidate();
        setCancelModalOpen(false);
      }
    }
  });
  const activateMutation = useActivate({ mutation: { onSuccess: invalidate } });
  const holdMutation = useHold({ mutation: { onSuccess: invalidate } });
  const completeMutation = useComplete({ mutation: { onSuccess: invalidate } });
  const closeMutation = useCloseContract({ mutation: { onSuccess: invalidate } });
  const deactivateMutation = useDeactivateContract({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
        navigate('/contracts');
      },
      onError: (error) => {
        setDeleteError(contractLineErrorMessage(error, t('common.error')));
        setDeleteModalOpen(false);
      }
    }
  });

  if (isLoading || !contract) {
    return (
      <Container size="lg" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  const config = statusConfig[contract.status ?? ContractStatus.DRAFT];
  const trackerSteps = buildContractTrackerSteps(
    contract.status ?? ContractStatus.DRAFT,
    isDraftAuthor
  );
  const isSent = contract.status === ContractStatus.SENT;
  const isAccepted = contract.status === ContractStatus.ACCEPTED;
  const isRejected = contract.status === ContractStatus.REJECTED;
  const isTerminal =
    contract.status === ContractStatus.COMPLETED ||
    contract.status === ContractStatus.CLOSED ||
    contract.status === ContractStatus.CANCELLED;
  // CON-09 (obrnuti smjer): kod clientInitiated ugovora Partner odlučuje (Prihvati/Odbij), ne Klijent.
  const decidingOrgId = contract.clientInitiated ? contract.partnerOrgId : contract.clientOrgId;
  const canAct = isSent && !!user && user.organisationId === decidingOrgId;
  // DRAFT ima svoju, strožu akciju (Delete, vidi canDelete) — Cancel i Delete se namjerno
  // nikad ne preklapaju, svaki status ima točno jednu "ukloni ugovor" akciju.
  const canCancel = !isTerminal && contract.status !== ContractStatus.DRAFT && !!user;
  // CON-02 izvršni prijelazi — isti obrazac kao Cancel (bilo koja strana na svojoj kopiji,
  // backend propagira status na obje kopije, vidi ContractService.propagateStatus).
  const canActivate =
    (contract.status === ContractStatus.ACCEPTED || contract.status === ContractStatus.ON_HOLD) &&
    !!user;
  const canHold = contract.status === ContractStatus.ACTIVE && !!user;
  const canComplete = contract.status === ContractStatus.ACTIVE && !!user;
  const canClose = contract.status === ContractStatus.COMPLETED && !!user;
  const activateLabel = t(
    contract.status === ContractStatus.ON_HOLD ? 'contractDetail.resume' : 'contractDetail.activate'
  );
  const canEdit =
    (contract.status === ContractStatus.DRAFT || contract.status === ContractStatus.REJECTED) &&
    !!user &&
    isDraftAuthor;
  const canDelete = contract.status === ContractStatus.DRAFT && !!user && isDraftAuthor;
  // "Razrada prostora" (contract_space_line_plans) ima isti gate kao stavke ugovora — samo
  // DRAFT/REJECTED, samo autorska strana (vidi WorkStructureService.assertEditable + isDraftAuthor
  // gore). Ovo je DRAFT-time plan, nikad ne gleda work_packages/planned_items — ostaje isti prikaz
  // (read-only izvan DRAFT/REJECTED) bez obzira koliko je ugovor odmakao (ACTIVE/ON_HOLD/...).
  const canManageWorkStructure =
    isDraftAuthor &&
    (contract.status === ContractStatus.DRAFT || contract.status === ContractStatus.REJECTED);
  // Prikazni placeholder tabovi (Radni paketi/Aktivnosti/Tjedni izvještaji/Fakture) — najavljuju
  // funkcionalnost koja dolazi u idućoj fazi, vidljivi tek nakon prihvaćanja+aktivacije ugovora
  // (ACTIVE i svaki sljedeći status), ne prije (DRAFT/SENT/ACCEPTED) i ne za odbijen ugovor.
  const preActivationStatuses: ContractStatus[] = [
    ContractStatus.DRAFT,
    ContractStatus.SENT,
    ContractStatus.ACCEPTED,
    ContractStatus.REJECTED
  ];
  // Operativni tabovi pripadaju izvođaču (partneru); klijent vidi samo stavke i razradu prostora.
  const showUpcomingOperationalTabs =
    isPartnerView && !preActivationStatuses.includes(contract.status ?? ContractStatus.DRAFT);
  const contractLabel = contract.contractNumber ?? contract.referenceId;
  const hasPriceList = !!contract.priceListId;
  const existingCategories = Array.from(
    new Set(lines.map((line) => line.category).filter(Boolean))
  ) as string[];

  // Ukupno ispod stavki prati nacrt (uključuje nespremljene promjene); sažetak realizacije ostaje
  // na spremljenim stavkama.
  const savedSubtotal = lines.reduce((sum, line) => sum + (line.totalPrice ?? 0), 0);
  const subtotal = displayLines
    .filter((line) => !line.isRemoved)
    .reduce((sum, line) => sum + lineAmount(line), 0);
  const vatRate = contract.vatRate ?? 0;
  const showVat = !!contract.vatEnabled && vatRate > 0;
  const vat = showVat ? (subtotal * vatRate) / 100 : 0;
  const total = subtotal + vat;

  // Planirani/realizirani sati i cijena/sat — vidi "Napomene o izračunu" (UC-15):
  // planirani sati = količina × normativ, realizirani sati = realizirana količina ×
  // normativ (isti normativ po jedinici kao planirano), odstupanje = planirano − realizirano.
  const plannedHours = lines.reduce(
    (sum, line) => sum + (line.quantity ?? 0) * (line.normHours ?? 0),
    0
  );
  const plannedValue = savedSubtotal;
  const plannedRate = plannedHours > 0 ? plannedValue / plannedHours : 0;

  const realizedHours = lines.reduce(
    (sum, line) => sum + (line.realizedQty ?? 0) * (line.normHours ?? 0),
    0
  );
  const realizedValue = lines.reduce((sum, line) => sum + (line.realizedAmount ?? 0), 0);
  const realizedRate = realizedHours > 0 ? realizedValue / realizedHours : 0;

  const deviationHours = plannedHours - realizedHours;
  const deviationValue = plannedValue - realizedValue;
  // Crveno SAMO kad realizacija stvarno prijeđe plan (negativno odstupanje) —
  // dok je realizacija još ispod plana, to je normalan tijek posla, ne greška.
  const isDeviationOverrun = deviationHours < 0 || deviationValue < 0;

  // Traka UKUPNO ispod stavki = zbroj TRENUTNE stranice; sažetak ugovora (kartice) ostaje na cijelom ugovoru.
  const pagePlannedValue = pageLines.reduce((sum, line) => sum + (line.totalPrice ?? 0), 0);
  const pageRealizedValue = pageLines.reduce((sum, line) => sum + (line.realizedAmount ?? 0), 0);
  const pageRemainingValue = pagePlannedValue - pageRealizedValue;
  const pagePlannedHours = pageLines.reduce(
    (sum, line) => sum + (line.quantity ?? 0) * (line.normHours ?? 0),
    0
  );

  const contractTypeLabel = t(
    contract.contractType === ContractContractType.FRAMEWORK
      ? 'createContract.contractTypeFramework'
      : 'createContract.contractTypeFixed'
  );
  const paymentTermsValue = contract.paymentTerms
    ? t('contractDetail.paymentTermsValue', { days: contract.paymentTerms })
    : '—';
  const additionalWorkPriceValue =
    contract.additionalWorkPrice != null ? `${formatAmount(contract.additionalWorkPrice)}/h` : '—';

  const infoField = (label: string, value: ReactNode, caption?: string) => (
    <Stack gap={2} key={label}>
      <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}>
        {label}
      </Text>
      {typeof value === 'string' ? (
        <Text fz={13.5} style={{ color: 'var(--color-text-primary)' }}>
          {value}
        </Text>
      ) : (
        value
      )}
      {caption && (
        <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
          {caption}
        </Text>
      )}
    </Stack>
  );

  const infoColumnDivider = (
    <div style={{ width: 1, alignSelf: 'stretch', backgroundColor: 'var(--color-border-soft)' }} />
  );

  const columns: [string, number | undefined, 'left' | 'right' | 'center'][] = canEdit
    ? [
        [t('contractDetail.colCode'), TABLE_ITEM_CODE_COL_WIDTH, 'left'],
        [t('contractDetail.colDescription'), undefined, 'left'],
        [t('contractDetail.colUnit'), 60, 'center'],
        [t('contractDetail.colPrice'), 110, 'right'],
        [t('contractDetail.colEditQty'), 100, 'right'],
        [t('contractDetail.colEditAmount'), 100, 'right'],
        [t('createContract.colRemove'), 60, 'right']
      ]
    : [
        [t('contractDetail.colCode'), TABLE_ITEM_CODE_COL_WIDTH, 'left'],
        [t('contractDetail.colDescription'), undefined, 'left'],
        [t('contractDetail.colUnit'), 60, 'center'],
        [t('contractDetail.colPrice'), 90, 'right'],
        [t('contractDetail.colContracted'), 100, 'right'],
        [t('contractDetail.colRealized'), 100, 'right'],
        [t('contractDetail.colRemaining'), 100, 'right'],
        [t('contractDetail.colNormHours'), 90, 'right'],
        [t('contractDetail.colPlanHours'), 80, 'right']
      ];

  // Ad-hoc stavke (bez cjenika) se dodaju kroz vlastitu formu sa zasebnim spremanjem — s
  // nespremljenim nacrtom prvo pitamo, da se dva načina spremanja ne miješaju.
  const openAddItems = () => {
    if (hasPriceList) setAddItemModalOpen(true);
    else guard(() => setAddItemModalOpen(true));
  };

  const renderLineAction = (row: DisplayLine) =>
    row.isRemoved ? (
      <UndoRemoveButton onClick={() => removeLine(row)} />
    ) : (
      <RemoveLineButton onClick={() => removeLine(row)} />
    );

  const hasDraftState = (row: DisplayLine) =>
    row.isNew || row.isRemoved || row.isChanged || !!row.quantityError || !!row.priceError;

  const renderEditRow = (row: DisplayLine, index: number) => {
    const mutedText = row.isRemoved ? 'var(--color-text-muted)' : undefined;
    return (
      <div
        key={row.key}
        style={{
          ...tableRowStyle,
          padding: '14px 20px',
          borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)',
          backgroundColor: hasDraftState(row) ? DRAFT_ROW_BACKGROUND : undefined
        }}
      >
        <Text
          fz={13.5}
          fw={600}
          style={{
            color: mutedText ?? 'var(--color-text-primary)',
            width: TABLE_ITEM_CODE_COL_WIDTH
          }}
        >
          {row.itemCode ?? '—'}
        </Text>
        <Group gap={8} wrap="nowrap" justify="space-between" style={{ flex: 1 }}>
          <Text
            fz={13}
            style={{
              color: mutedText ?? 'var(--color-text-secondary)',
              textDecoration: row.isRemoved ? 'line-through' : undefined
            }}
          >
            {row.description}
          </Text>
          {row.isNew && <NewItemBadge />}
        </Group>
        <Text
          fz={13}
          ta="center"
          style={{ color: mutedText ?? 'var(--color-text-secondary)', width: 60 }}
        >
          {findUnitLabel(unitsData, row.unitId, i18n.language)}
        </Text>
        <div style={{ width: 110, display: 'flex', justifyContent: 'flex-end' }}>
          <DraftNumberInput
            width={100}
            allowDecimal
            decimalScale={2}
            value={row.unitPrice}
            savedValue={row.savedUnitPrice}
            isNew={row.isNew}
            error={row.priceError}
            disabled={row.isRemoved}
            formatSaved={formatNumber}
            ariaLabel={t('contractDetail.colPrice')}
            onChange={(value) => setLineValue(row, 'unitPrice', value)}
          />
        </div>
        <div style={{ width: 100, display: 'flex', justifyContent: 'flex-end' }}>
          <DraftNumberInput
            width={70}
            value={row.quantity}
            savedValue={row.savedQuantity}
            isNew={row.isNew}
            error={row.quantityError}
            disabled={row.isRemoved}
            ariaLabel={t('contractDetail.colEditQty')}
            onChange={(value) => setLineValue(row, 'quantity', value)}
          />
        </div>
        <Text
          fz={13.5}
          ta="right"
          style={{ color: mutedText ?? 'var(--color-text-primary)', width: 100 }}
        >
          {formatAmount(lineAmount(row))}
        </Text>
        <Group w={60} justify="flex-end">
          {renderLineAction(row)}
        </Group>
      </div>
    );
  };

  const renderEditCard = (row: DisplayLine) => {
    const unitLabel = findUnitLabel(unitsData, row.unitId, i18n.language);
    return (
      <Stack
        key={row.key}
        gap={10}
        p={16}
        style={{
          borderRadius: 12,
          border: '1px solid var(--color-border-soft)',
          backgroundColor: hasDraftState(row) ? DRAFT_ROW_BACKGROUND : 'var(--color-bg-surface)'
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap={8} wrap="nowrap">
            <Text fz={12.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {t('contractDetail.cardCode', { code: row.itemCode ?? '—' })}
            </Text>
            {row.isNew && <NewItemBadge />}
          </Group>
          {renderLineAction(row)}
        </Group>
        <Text
          fz={13.5}
          style={{
            color: row.isRemoved ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
            textDecoration: row.isRemoved ? 'line-through' : undefined
          }}
        >
          {row.description}
        </Text>
        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
          {t('offerDetail.cardAmount')}{' '}
          <Text span fz={11} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {formatAmount(lineAmount(row))}
          </Text>
        </Text>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Text fz={13} pt={10} style={{ color: 'var(--color-text-secondary)' }}>
            {t('offerDetail.cardQuantity', { unit: unitLabel })}
          </Text>
          <DraftNumberInput
            width={80}
            height={40}
            value={row.quantity}
            savedValue={row.savedQuantity}
            isNew={row.isNew}
            error={row.quantityError}
            disabled={row.isRemoved}
            ariaLabel={t('contractDetail.colEditQty')}
            onChange={(value) => setLineValue(row, 'quantity', value)}
          />
        </Group>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Text fz={13} pt={10} style={{ color: 'var(--color-text-secondary)' }}>
            {t('offerDetail.cardPrice')}
          </Text>
          <DraftNumberInput
            width={110}
            height={40}
            allowDecimal
            decimalScale={2}
            value={row.unitPrice}
            savedValue={row.savedUnitPrice}
            isNew={row.isNew}
            error={row.priceError}
            disabled={row.isRemoved}
            formatSaved={formatNumber}
            ariaLabel={t('contractDetail.colPrice')}
            onChange={(value) => setLineValue(row, 'unitPrice', value)}
          />
        </Group>
      </Stack>
    );
  };

  const linesSectionContent = (
    <>
      <Box visibleFrom="sm" style={{ overflowX: 'auto' }}>
        <CardSection>
          <div style={{ minWidth: canEdit ? 700 : 1070 }}>
            <div
              className="table-header-row"
              style={{
                ...tableRowStyle,
                padding: '12px 20px',
                backgroundColor: 'var(--color-bg-canvas)',
                borderBottom: '1px solid var(--color-border-soft)'
              }}
            >
              {columns.map(([label, width, align]) => (
                <Text
                  key={label}
                  fz={10}
                  fw={600}
                  ta={align}
                  style={{
                    color: 'var(--color-text-muted)',
                    letterSpacing: 0.4,
                    width,
                    flex: width ? undefined : 1
                  }}
                >
                  {label}
                </Text>
              ))}
            </div>

            {canEdit
              ? pageRows.map((row, index) => renderEditRow(row, index))
              : pageLines.map((line, index) => {
                  const planHours = (line.quantity ?? 0) * (line.normHours ?? 0);
                  const remainingQty = (line.quantity ?? 0) - (line.realizedQty ?? 0);
                  const remainingValue = (line.totalPrice ?? 0) - (line.realizedAmount ?? 0);
                  return (
                    <div
                      key={line.id}
                      style={{
                        ...tableRowStyle,
                        padding: '14px 20px',
                        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Text
                        fz={13.5}
                        fw={600}
                        style={{
                          color: 'var(--color-text-primary)',
                          width: TABLE_ITEM_CODE_COL_WIDTH
                        }}
                      >
                        {line.itemCode ?? '—'}
                      </Text>
                      <Text fz={13} style={{ color: 'var(--color-text-secondary)', flex: 1 }}>
                        {line.description}
                      </Text>
                      <Text
                        fz={13}
                        ta="center"
                        style={{ color: 'var(--color-text-secondary)', width: 60 }}
                      >
                        {findUnitLabel(unitsData, line.unitId, i18n.language)}
                      </Text>
                      <Text
                        fz={13.5}
                        ta="right"
                        style={{ color: 'var(--color-text-primary)', width: 90 }}
                      >
                        {formatAmount(line.unitPrice)}
                      </Text>
                      <Stack gap={2} style={{ width: 100, alignItems: 'flex-end' }}>
                        <Text fz={13} ta="right" style={{ color: 'var(--color-text-primary)' }}>
                          {line.quantity}
                        </Text>
                        <Text fz={11} ta="right" style={{ color: 'var(--color-text-muted)' }}>
                          {formatAmount(line.totalPrice)}
                        </Text>
                      </Stack>
                      <Stack gap={2} style={{ width: 100, alignItems: 'flex-end' }}>
                        <Text fz={13} ta="right" style={{ color: 'var(--color-text-primary)' }}>
                          {line.realizedQty ?? 0}
                        </Text>
                        <Text fz={11} ta="right" style={{ color: 'var(--color-text-muted)' }}>
                          {formatAmount(line.realizedAmount ?? 0)}
                        </Text>
                      </Stack>
                      <Stack gap={2} style={{ width: 100, alignItems: 'flex-end' }}>
                        <Text
                          fz={13}
                          fw={600}
                          ta="right"
                          style={{
                            color:
                              remainingQty < 0
                                ? 'var(--color-status-danger)'
                                : 'var(--color-text-primary)'
                          }}
                        >
                          {remainingQty}
                        </Text>
                        <Text
                          fz={11}
                          ta="right"
                          style={{
                            color:
                              remainingValue < 0
                                ? 'var(--color-status-danger)'
                                : 'var(--color-text-muted)'
                          }}
                        >
                          {formatAmount(remainingValue)}
                        </Text>
                      </Stack>
                      <Text
                        fz={13}
                        ta="right"
                        style={{ color: 'var(--color-text-secondary)', width: 90 }}
                      >
                        {formatNumber(line.normHours)}
                      </Text>
                      <Text
                        fz={13}
                        ta="right"
                        style={{ color: 'var(--color-text-secondary)', width: 80 }}
                      >
                        {formatHours(planHours)}
                      </Text>
                    </div>
                  );
                })}

            {displayLines.length === 0 && (
              <Center py={40}>
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('contractDetail.noLines')}
                </Text>
              </Center>
            )}

            {canEdit && (
              <ItemsSaveBar
                summary={draftSummary}
                isSaving={saveLinesMutation.isPending}
                onCancel={discardDraft}
                onSave={saveDraft}
              />
            )}

            {!canEdit && lines.length > 0 && (
              <div
                style={{
                  ...tableRowStyle,
                  padding: '14px 20px',
                  borderTop: '2px solid var(--color-border-default)',
                  backgroundColor: 'var(--color-bg-canvas)'
                }}
              >
                <Text fz={13} style={{ color: 'var(--color-text-secondary)', width: 100 }} />
                <Text fz={13} fw={700} style={{ color: 'var(--color-text-primary)', flex: 1 }}>
                  {t('contractDetail.linesTotalRow')}
                </Text>
                <Text style={{ width: 60 }} />
                <div style={{ width: 90 }} />
                <Text
                  fz={13}
                  fw={700}
                  ta="right"
                  style={{ color: 'var(--color-text-primary)', width: 100 }}
                >
                  {formatAmount(pagePlannedValue)}
                </Text>
                <Text
                  fz={13}
                  fw={700}
                  ta="right"
                  style={{ color: 'var(--color-text-primary)', width: 100 }}
                >
                  {formatAmount(pageRealizedValue)}
                </Text>
                <Text
                  fz={13}
                  fw={700}
                  ta="right"
                  style={{
                    width: 100,
                    color:
                      pageRemainingValue < 0
                        ? 'var(--color-status-danger)'
                        : 'var(--color-text-primary)'
                  }}
                >
                  {formatAmount(pageRemainingValue)}
                </Text>
                <Text style={{ width: 90 }} />
                <Text
                  fz={13}
                  fw={700}
                  ta="right"
                  style={{ color: 'var(--color-text-primary)', width: 80 }}
                >
                  {formatHours(pagePlannedHours)}
                </Text>
                {canEdit && <Group w={50} />}
              </div>
            )}

            {displayLines.length > 0 && (
              <ListPagination
                label={t('contractDetail.linesShowing', {
                  shown: pageRows.length,
                  total: displayLines.length
                })}
                page={linesPage}
                totalPages={linesTotalPages}
                onPageChange={setLinesPage}
                pageSize={linesPageSize}
                onPageSizeChange={setLinesPageSize}
              />
            )}
          </div>
        </CardSection>
      </Box>

      <Stack hiddenFrom="sm" gap={12} p={16}>
        <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('contractDetail.itemsHeading')}
        </Text>

        {canEdit
          ? pageRows.map((row) => renderEditCard(row))
          : pageLines.map((line) => {
              const planHours = (line.quantity ?? 0) * (line.normHours ?? 0);
              const remaining = (line.quantity ?? 0) - (line.realizedQty ?? 0);
              const remainingValue = (line.totalPrice ?? 0) - (line.realizedAmount ?? 0);
              return (
                <Stack
                  key={line.id}
                  gap={10}
                  p={16}
                  style={{
                    borderRadius: 12,
                    border: '1px solid var(--color-border-soft)',
                    backgroundColor: 'var(--color-bg-surface)'
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Text fz={12} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                      {line.itemCode ?? '—'}
                    </Text>
                  </Group>
                  <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                    {line.description}
                  </Text>
                  <Group gap={12} wrap="wrap">
                    <Group gap={4} wrap="nowrap">
                      <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                        {t('contractDetail.qtyShort')}
                      </Text>
                      <Text fz={11} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
                        {line.quantity} {findUnitLabel(unitsData, line.unitId, i18n.language)}
                      </Text>
                    </Group>
                    <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                      |
                    </Text>
                    <Group gap={4} wrap="nowrap">
                      <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                        {t('contractDetail.priceShort')}
                      </Text>
                      <Text fz={11} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
                        {formatAmount(line.unitPrice)}
                      </Text>
                    </Group>
                  </Group>
                  <>
                    <Group gap={12} wrap="wrap">
                      <Group gap={4} wrap="nowrap" align="baseline">
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {t('contractDetail.colContracted')}
                        </Text>
                        <Text fz={11} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
                          {line.quantity}
                        </Text>
                        <Text fz={10} style={{ color: 'var(--color-text-muted)' }}>
                          ({formatAmount(line.totalPrice)})
                        </Text>
                      </Group>
                      <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                        |
                      </Text>
                      <Group gap={4} wrap="nowrap" align="baseline">
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {t('contractDetail.colRealized')}
                        </Text>
                        <Text fz={11} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
                          {line.realizedQty ?? 0}
                        </Text>
                        <Text fz={10} style={{ color: 'var(--color-text-muted)' }}>
                          ({formatAmount(line.realizedAmount ?? 0)})
                        </Text>
                      </Group>
                    </Group>
                    <Group gap={4} wrap="nowrap" align="baseline">
                      <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                        {t('contractDetail.colRemaining')}
                      </Text>
                      <Text
                        fz={11}
                        fw={600}
                        style={{
                          color:
                            remaining < 0
                              ? 'var(--color-status-danger)'
                              : 'var(--color-text-secondary)'
                        }}
                      >
                        {remaining}
                      </Text>
                      <Text
                        fz={10}
                        style={{
                          color:
                            remainingValue < 0
                              ? 'var(--color-status-danger)'
                              : 'var(--color-text-muted)'
                        }}
                      >
                        ({formatAmount(remainingValue)})
                      </Text>
                    </Group>
                    <Group gap={4} wrap="nowrap">
                      <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                        {t('contractDetail.colNormHours')}
                      </Text>
                      <Text fz={11} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
                        {formatNumber(line.normHours)}
                      </Text>
                    </Group>
                    <Group gap={4} wrap="nowrap">
                      <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                        {t('contractDetail.colPlanHours')}
                      </Text>
                      <Text fz={11} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
                        {formatHours(planHours)}
                      </Text>
                    </Group>
                  </>
                </Stack>
              );
            })}

        {displayLines.length === 0 && (
          <Center py={40}>
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('contractDetail.noLines')}
            </Text>
          </Center>
        )}

        {!canEdit && lines.length > 0 && (
          <Stack
            gap={10}
            p={16}
            style={{
              borderRadius: 12,
              border: '1px solid var(--color-border-default)',
              backgroundColor: 'var(--color-bg-canvas)'
            }}
          >
            <Text fz={12} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {t('contractDetail.linesTotalRow')}
            </Text>
            <Group gap={12} wrap="wrap">
              <Group gap={4} wrap="nowrap" align="baseline">
                <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                  {t('contractDetail.colContracted')}
                </Text>
                <Text fz={11} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                  {formatAmount(pagePlannedValue)}
                </Text>
              </Group>
              <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                |
              </Text>
              <Group gap={4} wrap="nowrap" align="baseline">
                <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                  {t('contractDetail.colRealized')}
                </Text>
                <Text fz={11} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                  {formatAmount(pageRealizedValue)}
                </Text>
              </Group>
            </Group>
            <Group gap={4} wrap="nowrap" align="baseline">
              <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                {t('contractDetail.colRemaining')}
              </Text>
              <Text
                fz={11}
                fw={700}
                style={{
                  color:
                    pageRemainingValue < 0
                      ? 'var(--color-status-danger)'
                      : 'var(--color-text-primary)'
                }}
              >
                {formatAmount(pageRemainingValue)}
              </Text>
            </Group>
            <Group gap={4} wrap="nowrap">
              <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                {t('contractDetail.colPlanHours')}
              </Text>
              <Text fz={11} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                {formatHours(pagePlannedHours)}
              </Text>
            </Group>
          </Stack>
        )}

        {displayLines.length > 0 && (
          <ListPagination
            label={t('contractDetail.linesShowing', {
              shown: pageRows.length,
              total: displayLines.length
            })}
            page={linesPage}
            totalPages={linesTotalPages}
            onPageChange={setLinesPage}
            pageSize={linesPageSize}
            onPageSizeChange={setLinesPageSize}
          />
        )}
      </Stack>
    </>
  );

  const contractTotalContent = (
    <>
      <Stack gap={16} align="flex-end" visibleFrom="sm">
        <Stack
          gap={12}
          p={20}
          style={{
            width: 340,
            maxWidth: '100%',
            borderRadius: 12,
            border: '1px solid var(--color-border-soft)',
            backgroundColor: 'var(--color-bg-surface)'
          }}
        >
          {showVat && (
            <Group justify="space-between" wrap="nowrap">
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('contractDetail.subtotal')}
              </Text>
              <Text fz={14} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                {formatAmount(subtotal)}
              </Text>
            </Group>
          )}
          {showVat && (
            <Group justify="space-between" wrap="nowrap">
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('contractDetail.vat', { rate: vatRate })}
              </Text>
              <Text fz={14} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                {formatAmount(vat)}
              </Text>
            </Group>
          )}
          {showVat && divider}
          <Group justify="space-between" wrap="nowrap">
            <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {t('contractDetail.totalFooter')}
            </Text>
            <Text fz={18} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {formatAmount(total)}
            </Text>
          </Group>
          {draftSummary.total > 0 && (
            <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
              {t('itemsDraft.includesUnsaved')}
            </Text>
          )}
        </Stack>
      </Stack>

      <Stack
        hiddenFrom="sm"
        gap={10}
        p={16}
        style={{
          borderRadius: 12,
          border: '1px solid var(--color-border-soft)',
          backgroundColor: 'var(--color-bg-surface)'
        }}
      >
        {showVat && (
          <Group justify="space-between" wrap="nowrap">
            <Text fz={12.5} style={{ color: 'var(--color-text-muted)' }}>
              {t('contractDetail.subtotal')}
            </Text>
            <Text fz={13.5} fw={500} style={{ color: 'var(--color-text-primary)' }}>
              {formatAmount(subtotal)}
            </Text>
          </Group>
        )}
        {showVat && (
          <Group justify="space-between" wrap="nowrap">
            <Text fz={12.5} style={{ color: 'var(--color-text-muted)' }}>
              {t('contractDetail.vat', { rate: vatRate })}
            </Text>
            <Text fz={13.5} fw={500} style={{ color: 'var(--color-text-primary)' }}>
              {formatAmount(vat)}
            </Text>
          </Group>
        )}
        {showVat && divider}
        <Group justify="space-between" wrap="nowrap">
          <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {t('contractDetail.totalFooter')}
          </Text>
          <Text fz={17} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {formatAmount(total)}
          </Text>
        </Group>
        {draftSummary.total > 0 && (
          <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
            {t('itemsDraft.includesUnsaved')}
          </Text>
        )}
      </Stack>
    </>
  );

  const summaryRow = (label: string, value: string, valueColor?: string) => (
    <Group justify="space-between" key={label} w="100%">
      <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
        {label}
      </Text>
      <Text fz={13.5} fw={700} style={{ color: valueColor ?? 'var(--color-text-primary)' }}>
        {value}
      </Text>
    </Group>
  );

  type SummaryTone = 'info' | 'success' | 'danger' | 'neutral';

  const summaryCard = (
    title: string,
    subtitle: string | undefined,
    icon: ReactNode,
    tone: SummaryTone,
    rows: ReactNode
  ) => (
    <Stack
      gap={0}
      style={{
        flex: '1 1 260px',
        minWidth: 240,
        borderRadius: 8,
        border: `1px solid ${tone === 'danger' ? 'var(--color-status-danger)' : 'var(--color-border-soft)'}`,
        backgroundColor: 'var(--color-bg-surface)',
        overflow: 'hidden'
      }}
    >
      <Group
        gap={10}
        wrap="nowrap"
        px={16}
        py={12}
        style={{
          backgroundColor:
            tone === 'neutral' ? 'var(--color-bg-surface-2)' : `var(--color-status-${tone}-tint)`,
          width: '100%'
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            backgroundColor: 'var(--color-bg-surface)',
            color: `var(--color-status-${tone === 'neutral' ? 'info' : tone})`
          }}
        >
          {icon}
        </div>
        <Group gap={6} wrap="nowrap" align="baseline">
          <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {title}
          </Text>
          {subtitle && (
            <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
              {subtitle}
            </Text>
          )}
        </Group>
      </Group>
      <Stack gap={10} px={16} pt={14} pb={16}>
        {rows}
      </Stack>
    </Stack>
  );

  const realizationSummary = (
    <Stack
      gap={16}
      p={20}
      style={{
        borderRadius: 12,
        border: '1px solid var(--color-border-default)',
        backgroundColor: 'var(--color-bg-surface)',
        width: '100%'
      }}
    >
      <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
        {t('contractDetail.realizationSectionTitle')}
      </Text>
      <Group gap={16} wrap="wrap" align="stretch" style={{ width: '100%' }}>
        {summaryCard(
          t('contractDetail.plannedCardTitle'),
          t('contractDetail.plannedCardSubtitle'),
          <IconFigmaDocument size={16} />,
          'info',
          <>
            {summaryRow(t('contractDetail.plannedHours'), formatHours(plannedHours))}
            {summaryRow(t('contractDetail.plannedValue'), formatAmount(plannedValue))}
            {summaryRow(t('contractDetail.plannedRate'), formatAmount(plannedRate))}
          </>
        )}

        {summaryCard(
          t('contractDetail.realizedCardTitle'),
          t('contractDetail.realizedCardSubtitle'),
          <IconFigmaCheck size={16} />,
          'success',
          <>
            {summaryRow(t('contractDetail.realizedHours'), formatHours(realizedHours))}
            {summaryRow(t('contractDetail.realizedValue'), formatAmount(realizedValue))}
            {summaryRow(t('contractDetail.realizedRate'), formatAmount(realizedRate))}
          </>
        )}

        {summaryCard(
          t('contractDetail.remainingCardTitle'),
          undefined,
          isDeviationOverrun ? <IconFigmaAlertTriangle size={16} /> : <IconFigmaInfo size={16} />,
          isDeviationOverrun ? 'danger' : 'neutral',
          <>
            {summaryRow(
              t('contractDetail.remainingHours'),
              formatHours(deviationHours),
              isDeviationOverrun ? 'var(--color-status-danger)' : undefined
            )}
            {summaryRow(
              t('contractDetail.remainingValue'),
              formatAmount(deviationValue),
              isDeviationOverrun ? 'var(--color-status-danger)' : undefined
            )}
          </>
        )}
      </Group>
      <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
        {t('contractDetail.realizationCaption')}
      </Text>
    </Stack>
  );

  // Isti uvjet kao banner u ContractLineAllocationModal: svaka važeća stavka raspoređena po
  // prostorima u punoj ugovorenoj količini — za obje vrste ugovora (razrada = ugovor).
  const allocatedQtyByLineId = new Map(
    (allocationsData ?? []).map((a) => [a.contractLineId, a.allocatedQty ?? 0])
  );
  const allLinesAllocated =
    lines.length > 0 &&
    lines.every((line) => (allocatedQtyByLineId.get(line.id) ?? 0) >= (line.quantity ?? 0));

  const planningTabs: CardTab[] = [
    {
      key: 'lines',
      label: t('contractDetail.tabAllLines'),
      headerRight: canEdit && (
        <Group gap={16} wrap="nowrap">
          <ItemsSaveStatus summary={draftSummary} justSaved={justSaved} savedAt={savedAt} />
          <Button size="sm" variant="outline" onClick={openAddItems}>
            {t(hasPriceList ? 'createContract.addItem' : 'contractLineForm.addItemButton')}
          </Button>
        </Group>
      ),
      content: linesSectionContent
    },
    {
      key: 'spaces',
      label: t('contractDetail.tabSpaceBreakdown'),
      captionInfo:
        spaceDraftState.notFullyAllocated > 0 ? (
          <Group
            gap={8}
            wrap="nowrap"
            h={40}
            px={14}
            style={{
              borderRadius: 8,
              border: '1px solid var(--color-accent-primary)',
              backgroundColor: 'var(--color-row-draft)'
            }}
          >
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'var(--color-accent-primary)'
              }}
            />
            <Text fz={13.5} fw={500} style={{ color: 'var(--color-text-primary)' }}>
              {t(
                spaceSummary.total > 0
                  ? 'contractSpaceBreakdown.notFullyAllocatedAfterSave'
                  : 'contractSpaceBreakdown.notFullyAllocated',
                { count: spaceDraftState.notFullyAllocated }
              )}
            </Text>
          </Group>
        ) : (
          allLinesAllocated && (
            <Group
              gap={8}
              wrap="nowrap"
              h={40}
              px={14}
              style={{
                borderRadius: 8,
                border: '1px solid var(--color-status-success)',
                backgroundColor: 'var(--color-status-success-soft)',
                color: 'var(--color-status-success)'
              }}
            >
              <IconFigmaCheck size={16} />
              <Text fz={14} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                {t('contractDetail.allLinesAllocated')}
              </Text>
            </Group>
          )
        ),
      headerRight: (
        <Group gap={8}>
          <Button variant="outline" onClick={() => setLineAllocationModalOpen(true)}>
            {t('contractDetail.reviewAllocation')}
          </Button>
          {canManageWorkStructure && (
            <AddActionButton onClick={() => setSpaceSelectionModalOpen(true)}>
              {t('contractDetail.editSelection')}
            </AddActionButton>
          )}
        </Group>
      ),
      content: (
        <Box p={20}>
          <ContractSpaceBreakdown
            contractId={id ?? ''}
            contractLabel={contract.contractNumber ?? contract.referenceId}
            coveredSpaces={coveredSpaces}
            readOnly={!canManageWorkStructure}
            guard={guard}
            onDraftChange={setSpaceDraftState}
            draftRef={spaceDraftRef}
          />
        </Box>
      )
    },
    ...(showUpcomingOperationalTabs
      ? ([
          { key: 'workPackages', label: t('nav.workPackages') },
          { key: 'activities', label: t('nav.activities') },
          { key: 'weekReports', label: t('nav.weeklyReports') },
          { key: 'invoices', label: t('nav.invoices') }
        ].map((tab) => ({
          ...tab,
          disabled: true,
          content: (
            <Center py={32}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('common.comingSoon')}
              </Text>
            </Center>
          )
        })) as CardTab[])
      : [])
  ];

  const additionalWorkPriceChip = (
    <Group
      gap={10}
      wrap="nowrap"
      px={14}
      py={12}
      style={{
        backgroundColor: 'var(--color-bg-surface-2)',
        borderRadius: 8,
        width: '100%'
      }}
    >
      <IconFigmaEuro size={16} color="var(--color-text-muted)" />
      <Text
        fz={11}
        fw={600}
        style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4, flex: 1 }}
      >
        {t('contractDetail.additionalWorkPrice')}
      </Text>
      <Text fz={15} fw={700} style={{ color: 'var(--color-text-primary)', whiteSpace: 'nowrap' }}>
        {additionalWorkPriceValue}
      </Text>
    </Group>
  );

  const billingInfoBox = (
    <Stack
      gap={12}
      p={14}
      style={{ backgroundColor: 'var(--color-bg-surface-2)', borderRadius: 8, width: '100%' }}
    >
      <Stack gap={2}>
        <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
          {t('contractDetail.billingInfoTitle')}
        </Text>
        <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
          {t('contractDetail.billingInfoCaption')}
        </Text>
      </Stack>
      {infoField(t('contractDetail.billingLabel'), contract.billingLabel || '—')}
      {infoField(t('contractDetail.ourReference'), contract.ourReference || '—')}
    </Stack>
  );

  const acceptedTrailing = isAccepted && (
    <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
      {t('contractDetail.acceptedBy')}{' '}
      <Text span fz={11} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
        {contract.decidedByName ?? '—'}
      </Text>
      {'  ·  '}
      {t('contractDetail.acceptedDate')}{' '}
      <Text span fz={11} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
        {formatDate(contract.updatedAt)}
      </Text>
    </Text>
  );

  const generalInfoContent = (
    <>
      <Box visibleFrom="sm" style={{ width: '100%' }}>
        <Stack
          gap={16}
          p={20}
          style={{
            borderRadius: 12,
            border: '1px solid var(--color-border-default)',
            backgroundColor: 'var(--color-bg-surface)'
          }}
        >
          <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {t('contractDetail.generalInfo')}
          </Text>
          <Group gap={24} align="flex-start" wrap="nowrap" style={{ width: '100%' }}>
            <Stack gap={14} style={{ flex: '1 1 0', minWidth: 0 }}>
              {infoField(t('contractDetail.partner'), otherPartyName(contract, user?.orgName))}
              {infoField(
                t('contractDetail.object'),
                contract.objectId ? (
                  <LinkValue
                    value={`${contract.objectCode ?? '—'} — ${contract.objectName ?? '—'} - ${formatObjectType(t, contract.objectType)}`}
                    onClick={() => navigate(`/templates/object-registry/${contract.objectId}`)}
                  />
                ) : (
                  '—'
                )
              )}
              {infoField(
                t('contractDetail.linkedOffer'),
                contract.offerId ? (
                  <LinkValue
                    value={`#${linkedOffer?.offerNumber ?? linkedOffer?.referenceId ?? '…'}`}
                    onClick={() => navigate(`/offers/${contract.offerId}`)}
                  />
                ) : (
                  '—'
                )
              )}
              {infoField(t('contractDetail.contractType'), contractTypeLabel)}
            </Stack>

            {infoColumnDivider}

            <Stack gap={14} style={{ flex: '1 1 0', minWidth: 0 }}>
              {infoField(t('contractDetail.contractDate'), formatDate(contract.createdAt))}
              {infoField(
                t('contractDetail.periodDates'),
                formatDateRange(contract.startDate, contract.endDate),
                formatWeekYearRangeFromDates(contract.startDate, contract.endDate)
              )}
              {infoField(t('contractDetail.paymentTerms'), paymentTermsValue)}
              {infoField(
                t('contractDetail.totalValue'),
                <Text fz={13.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                  {formatAmount(contract.contractValue)}
                </Text>
              )}
            </Stack>

            {infoColumnDivider}

            <Stack gap={14} style={{ width: 340, flexShrink: 0 }}>
              {additionalWorkPriceChip}
              {infoField(
                t('contractDetail.coordinator'),
                contract.coordinator || '—',
                t('contractDetail.coordinatorCaption')
              )}
              {billingInfoBox}
            </Stack>
          </Group>

          {acceptedTrailing && (
            <>
              <div style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }} />
              {acceptedTrailing}
            </>
          )}
        </Stack>
      </Box>

      <Stack hiddenFrom="sm" gap={16} w="100%">
        <Stack
          gap={14}
          p={16}
          style={{
            borderRadius: 12,
            border: '1px solid var(--color-border-default)',
            backgroundColor: 'var(--color-bg-surface)'
          }}
        >
          <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {t('contractDetail.generalInfo')}
          </Text>
          {infoField(t('contractDetail.partner'), otherPartyName(contract, user?.orgName))}
          {infoField(
            t('contractDetail.object'),
            contract.objectId ? (
              <LinkValue
                value={`${contract.objectCode ?? '—'} — ${contract.objectName ?? '—'} - ${formatObjectType(t, contract.objectType)}`}
                onClick={() => navigate(`/templates/object-registry/${contract.objectId}`)}
              />
            ) : (
              '—'
            )
          )}
          {infoField(
            t('contractDetail.linkedOffer'),
            contract.offerId ? (
              <LinkValue
                value={`#${linkedOffer?.offerNumber ?? linkedOffer?.referenceId ?? '…'}`}
                onClick={() => navigate(`/offers/${contract.offerId}`)}
              />
            ) : (
              '—'
            )
          )}
          {infoField(t('contractDetail.contractType'), contractTypeLabel)}
          {infoField(t('contractDetail.contractDate'), formatDate(contract.createdAt))}
          {infoField(
            t('contractDetail.periodDates'),
            formatDateRange(contract.startDate, contract.endDate),
            formatWeekYearRangeFromDates(contract.startDate, contract.endDate)
          )}
          {infoField(t('contractDetail.paymentTerms'), paymentTermsValue)}
          {infoField(
            t('contractDetail.totalValue'),
            <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {formatAmount(contract.contractValue)}
            </Text>
          )}
          {additionalWorkPriceChip}
          {infoField(
            t('contractDetail.coordinator'),
            contract.coordinator || '—',
            t('contractDetail.coordinatorCaption')
          )}
          {billingInfoBox}
          {acceptedTrailing && (
            <>
              <div style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }} />
              {acceptedTrailing}
            </>
          )}
        </Stack>
      </Stack>
    </>
  );

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to="/contracts" />

        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        {previewError && <Toast tone="danger">{t('contractDetail.previewError')}</Toast>}
        {deleteError && <Toast tone="danger">{deleteError}</Toast>}

        <Group justify="space-between" wrap="wrap" gap={12} visibleFrom="sm">
          <Group gap={12}>
            <Title order={2} fz={24} style={{ color: 'var(--color-text-primary)' }}>
              {t('contractDetail.title', { number: contractLabel })}
            </Title>
            <StatusPill
              label={t(contractStatusLabelKey(contract, user?.organisationId))}
              bg={config.bg}
              fg={config.fg}
              header
            />
          </Group>
          <Group gap={12}>
            {canEdit && (
              <MutationButton
                isPending={sendMutation.isPending}
                isPaused={sendMutation.isPaused}
                disabled={lines.length === 0}
                onClick={() => guard(() => setSendModalOpen(true))}
              >
                {t('contractDetail.sendButton')}
              </MutationButton>
            )}
            <MutationButton
              variant="filled"
              isPending={isPreviewLoading}
              onClick={() => guard(handlePreview)}
            >
              {t('contractDetail.previewButton')}
            </MutationButton>
            {canEdit && (
              <Button variant="outline" onClick={() => navigate(`/contracts/${id}/edit`)}>
                {t('contractFormEdit.title')}
              </Button>
            )}
            {canActivate && (
              <MutationButton
                variant="outline"
                isPending={activateMutation.isPending}
                isPaused={activateMutation.isPaused}
                onClick={() => id && activateMutation.mutate({ id })}
              >
                {activateLabel}
              </MutationButton>
            )}
            {canHold && (
              <MutationButton
                variant="outline"
                isPending={holdMutation.isPending}
                isPaused={holdMutation.isPaused}
                onClick={() => id && holdMutation.mutate({ id })}
              >
                {t('contractDetail.hold')}
              </MutationButton>
            )}
            {canComplete && (
              <MutationButton
                isPending={completeMutation.isPending}
                isPaused={completeMutation.isPaused}
                onClick={() => id && completeMutation.mutate({ id })}
              >
                {t('contractDetail.complete')}
              </MutationButton>
            )}
            {canClose && (
              <MutationButton
                variant="outline"
                isPending={closeMutation.isPending}
                isPaused={closeMutation.isPaused}
                onClick={() => id && closeMutation.mutate({ id })}
              >
                {t('contractDetail.close')}
              </MutationButton>
            )}
            {canCancel && (
              <Button color="red" variant="outline" onClick={() => setCancelModalOpen(true)}>
                {t('contractDetail.cancel')}
              </Button>
            )}
            {canDelete && (
              <Button
                color="red"
                variant="outline"
                onClick={() => guard(() => setDeleteModalOpen(true))}
              >
                {t('common.delete')}
              </Button>
            )}
            {canAct && (
              <>
                <Button color="red" variant="outline" onClick={() => setRejectModalOpen(true)}>
                  {t('contractDetail.reject')}
                </Button>
                <MutationButton
                  isPending={acceptMutation.isPending}
                  isPaused={acceptMutation.isPaused}
                  onClick={() => id && acceptMutation.mutate({ id })}
                >
                  {t('contractDetail.accept')}
                </MutationButton>
              </>
            )}
          </Group>
        </Group>
        <Stack gap={8} hiddenFrom="sm">
          <Title order={2} fz={22} style={{ color: 'var(--color-text-primary)' }}>
            {t('contractDetail.title', { number: contractLabel })}
          </Title>
          <StatusPill
            label={t(contractStatusLabelKey(contract, user?.organisationId))}
            bg={config.bg}
            fg={config.fg}
            header
          />
          <MobileActionBar>
            {canEdit && (
              <MutationButton
                isPending={sendMutation.isPending}
                isPaused={sendMutation.isPaused}
                disabled={lines.length === 0}
                onClick={() => guard(() => setSendModalOpen(true))}
                fullWidth
              >
                {t('contractDetail.sendButton')}
              </MutationButton>
            )}
            <MutationButton
              variant="filled"
              isPending={isPreviewLoading}
              onClick={() => guard(handlePreview)}
              fullWidth
            >
              {t('contractDetail.previewButton')}
            </MutationButton>
            {canEdit && (
              <Button variant="outline" fullWidth onClick={() => navigate(`/contracts/${id}/edit`)}>
                {t('contractFormEdit.title')}
              </Button>
            )}
            {canActivate && (
              <MutationButton
                variant="outline"
                fullWidth
                isPending={activateMutation.isPending}
                isPaused={activateMutation.isPaused}
                onClick={() => id && activateMutation.mutate({ id })}
              >
                {activateLabel}
              </MutationButton>
            )}
            {canHold && (
              <MutationButton
                variant="outline"
                fullWidth
                isPending={holdMutation.isPending}
                isPaused={holdMutation.isPaused}
                onClick={() => id && holdMutation.mutate({ id })}
              >
                {t('contractDetail.hold')}
              </MutationButton>
            )}
            {canComplete && (
              <MutationButton
                fullWidth
                isPending={completeMutation.isPending}
                isPaused={completeMutation.isPaused}
                onClick={() => id && completeMutation.mutate({ id })}
              >
                {t('contractDetail.complete')}
              </MutationButton>
            )}
            {canClose && (
              <MutationButton
                variant="outline"
                fullWidth
                isPending={closeMutation.isPending}
                isPaused={closeMutation.isPaused}
                onClick={() => id && closeMutation.mutate({ id })}
              >
                {t('contractDetail.close')}
              </MutationButton>
            )}
            {canCancel && (
              <Button
                color="red"
                variant="outline"
                fullWidth
                onClick={() => setCancelModalOpen(true)}
              >
                {t('contractDetail.cancel')}
              </Button>
            )}
            {canDelete && (
              <Button
                color="red"
                variant="outline"
                fullWidth
                onClick={() => guard(() => setDeleteModalOpen(true))}
              >
                {t('common.delete')}
              </Button>
            )}
            {canAct && (
              <Button
                color="red"
                variant="outline"
                fullWidth
                onClick={() => setRejectModalOpen(true)}
              >
                {t('contractDetail.reject')}
              </Button>
            )}
            {canAct && (
              <MutationButton
                isPending={acceptMutation.isPending}
                isPaused={acceptMutation.isPaused}
                onClick={() => id && acceptMutation.mutate({ id })}
                fullWidth
              >
                {t('contractDetail.accept')}
              </MutationButton>
            )}
          </MobileActionBar>
        </Stack>

        {isAccepted && <StatusBanner tone="success" message={t('contractDetail.acceptedBanner')} />}

        <StatusTracker steps={trackerSteps} />

        {generalInfoContent}

        {!canEdit && realizationSummary}

        {isRejected && (
          <Box
            p={16}
            style={{
              borderRadius: 12,
              border: '1px solid var(--color-status-danger)',
              backgroundColor: 'var(--color-status-danger-soft)'
            }}
          >
            <Stack gap={10}>
              <Text fz={13} fw={700} style={{ color: 'var(--color-status-danger)' }}>
                {t('contractDetail.rejectionDetailsTitle')}
              </Text>
              <Group gap={24} wrap="wrap">
                <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                  <Text component="span" fw={600} style={{ color: 'var(--color-text-primary)' }}>
                    {t('contractDetail.rejectedBy')}
                  </Text>{' '}
                  {contract.decidedByName ?? '—'}
                </Text>
                <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                  <Text component="span" fw={600} style={{ color: 'var(--color-text-primary)' }}>
                    {t('contractDetail.rejectedDateInline')}
                  </Text>{' '}
                  {formatDate(contract.updatedAt)}
                </Text>
              </Group>
              <Stack gap={4}>
                <Text
                  fz={11}
                  fw={600}
                  tt="uppercase"
                  style={{ color: 'var(--color-status-danger)', letterSpacing: 0.4 }}
                >
                  {t('contractDetail.rejectionReasonLabel')}
                </Text>
                <Text fz={13} style={{ color: 'var(--color-text-primary)' }}>
                  {contract.rejectionReason}
                </Text>
              </Stack>
            </Stack>
          </Box>
        )}

        {/* Obje strane uvijek vide Stavke i Razradu prostora; razradu uređuje samo strana koja je
            kreirala ugovor (canManageWorkStructure, dok je DRAFT/REJECTED), druga strana je vidi
            read-only. Operativni tabovi (radni paketi, aktivnosti, …) samo izvođaču (partneru). */}
        {saveError && (
          <StatusBanner tone="danger" message={saveError} onDismiss={() => setSaveError(null)} />
        )}

        <CardTabs
          tabs={planningTabs}
          active={activePlanningTab}
          onChange={(key) => guard(() => setActivePlanningTab(key))}
        />

        {contractTotalContent}

        {isRejected && (
          <StatusBanner
            tone="info"
            icon={<IconFigmaInfo size={16} />}
            message={t(
              contract.clientInitiated
                ? 'contractDetail.rejectedNextStepsClientInitiated'
                : 'contractDetail.rejectedNextSteps'
            )}
          />
        )}
      </Stack>

      <CancelContractModal
        opened={cancelModalOpen}
        contractLabel={contractLabel ?? ''}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={() => id && cancelMutation.mutate({ id })}
        loading={cancelMutation.isPending}
        isPaused={cancelMutation.isPaused}
      />

      <ConfirmActionModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => id && deactivateMutation.mutate({ id })}
        title={t('contractDetail.deleteConfirmTitle')}
        message={t('contractDetail.deleteConfirmMessage', { number: contractLabel })}
        confirmLabel={t('common.delete')}
        danger
        loading={deactivateMutation.isPending}
        isPaused={deactivateMutation.isPaused}
      />

      <RejectContractModal
        opened={rejectModalOpen}
        contractLabel={contractLabel ?? ''}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={(reason) => id && rejectMutation.mutate({ id, data: { reason } })}
        loading={rejectMutation.isPending}
        isPaused={rejectMutation.isPaused}
      />

      {canEdit && id && (
        <>
          <SendContractModal
            opened={sendModalOpen}
            contractLabel={contractLabel ?? ''}
            onClose={() => setSendModalOpen(false)}
            onConfirm={() => sendMutation.mutate({ id })}
            loading={sendMutation.isPending}
            isPaused={sendMutation.isPaused}
          />

          {hasPriceList ? (
            <ContractItemPickerModal
              opened={addItemModalOpen}
              onClose={() => setAddItemModalOpen(false)}
              priceListId={contract.priceListId}
              existingPriceListItemIds={[
                ...lines.map((line) => line.priceListItemId),
                ...draft.added.map((item) => item.meta.id)
              ].filter((itemId): itemId is string => !!itemId)}
              onAdd={addPriceListItems}
            />
          ) : (
            <ContractLineFormModal
              opened={addItemModalOpen}
              onClose={() => setAddItemModalOpen(false)}
              contractId={id}
              existingCategories={existingCategories}
              onSaved={setSavedMessage}
            />
          )}
        </>
      )}

      {canManageWorkStructure && id && (
        <ContractSpaceSelectionModal
          opened={spaceSelectionModalOpen}
          onClose={() => setSpaceSelectionModalOpen(false)}
          objectId={contract.objectId ?? ''}
          contractId={id}
          currentSpaceIds={coveredSpaces
            .map((space) => space.id)
            .filter((spaceId): spaceId is string => !!spaceId)}
        />
      )}

      {canEdit && activePlanningTab === 'lines' && (
        <Box hiddenFrom="sm">
          <ItemsSaveBar
            variant="mobile"
            summary={draftSummary}
            isSaving={saveLinesMutation.isPending}
            onCancel={discardDraft}
            onSave={saveDraft}
          />
        </Box>
      )}

      {unsavedChangesModal}

      {id && (
        <ContractLineAllocationModal
          opened={lineAllocationModalOpen}
          onClose={() => setLineAllocationModalOpen(false)}
          contractId={id}
        />
      )}
    </Container>
  );
};
